<template>
  <div class="page-container">
    <div class="page-header">
      <div>
        <h2 class="page-title">操作日志</h2>
      </div>
    </div>

    <el-card class="search-card" shadow="never">
      <div class="search-bar">
        <el-form :inline="true" :model="query" size="small" class="search-form">
          <el-form-item label="操作人">
            <el-input v-model="query.admin_name" placeholder="姓名" clearable style="width: 130px" @keyup.enter.native="handleSearch" />
          </el-form-item>
          <el-form-item label="功能模块">
            <el-select v-model="query.module" placeholder="全部" clearable style="width: 130px">
              <el-option v-for="m in modules" :key="m.value" :label="m.label" :value="m.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="操作类型">
            <el-select v-model="query.operation" placeholder="全部" clearable style="width: 130px">
              <el-option v-for="o in operations" :key="o.value" :label="o.label" :value="o.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="结果">
            <el-select v-model="query.result" placeholder="全部" clearable style="width: 110px">
              <el-option label="成功" :value="1" />
              <el-option label="失败" :value="0" />
            </el-select>
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
        <div class="action-bar">
          <el-button size="small" icon="el-icon-download" :loading="exporting" @click="handleExport">导出 Excel</el-button>
        </div>
      </div>
    </el-card>

    <el-card class="table-card" shadow="never">
      <el-table v-loading="loading" :data="list" border size="small" height="100%">
        <el-table-column prop="admin_name" label="操作人" width="100"/>
        <el-table-column label="模块" width="110">
          <template slot-scope="scope">{{ moduleLabel(scope.row.module) }}</template>
        </el-table-column>
        <el-table-column label="操作类型" width="100">
          <template slot-scope="scope">
            <el-tag size="mini" :type="operationTag(scope.row.operation)" effect="plain">{{ operationLabel(scope.row.operation) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="业务对象" min-width="150" show-overflow-tooltip>
          <template slot-scope="scope">
            {{ bizLabel(scope.row.biz_type) }}<span v-if="scope.row.biz_id">#{{ scope.row.biz_id }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="request_url" label="请求地址" min-width="200" show-overflow-tooltip/>
        <el-table-column prop="method" label="方法" width="80" align="center"/>
        <el-table-column prop="ip" label="操作IP" width="130"/>
        <el-table-column label="耗时" width="90" align="right">
          <template slot-scope="scope">
            <span :class="scope.row.duration > 1000 ? 'cell-slow' : ''">{{ scope.row.duration }} ms</span>
          </template>
        </el-table-column>
        <el-table-column label="结果" width="80" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.result === 1 ? 'success' : 'danger'" size="mini" effect="plain">
              {{ scope.row.result === 1 ? '成功' : '失败' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="操作时间" width="150"/>
        <el-table-column label="操作" width="80" align="center">
          <template slot-scope="scope">
            <el-button type="text" size="mini" @click="handleDetail(scope.row)">详情</el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrap">
        <el-pagination
          :current-page="page"
          :page-size="pageSize"
          :total="total"
          layout="total, prev, pager, next"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>

    <el-dialog title="日志详情" :visible.sync="detailVisible" width="720px" append-to-body v-loading="detailLoading">
      <el-descriptions :column="2" size="small" border v-if="currentRow">
        <el-descriptions-item label="操作人">{{ currentRow.admin_name }}</el-descriptions-item>
        <el-descriptions-item label="账号编号">{{ currentRow.admin_id }}</el-descriptions-item>
        <el-descriptions-item label="模块">{{ moduleLabel(currentRow.module) }}</el-descriptions-item>
        <el-descriptions-item label="操作类型">{{ operationLabel(currentRow.operation) }}</el-descriptions-item>
        <el-descriptions-item label="业务对象">{{ bizLabel(currentRow.biz_type) }}<span v-if="currentRow.biz_id">#{{ currentRow.biz_id }}</span></el-descriptions-item>
        <el-descriptions-item label="请求方法">{{ currentRow.method }}</el-descriptions-item>
        <el-descriptions-item label="请求地址" :span="2">{{ currentRow.request_url }}</el-descriptions-item>
        <el-descriptions-item label="操作IP">{{ currentRow.ip }}</el-descriptions-item>
        <el-descriptions-item label="归属地">{{ currentRow.ip_location || '—' }}</el-descriptions-item>
        <el-descriptions-item label="耗时">{{ currentRow.duration }} ms</el-descriptions-item>
        <el-descriptions-item label="结果">
          <el-tag :type="currentRow.result === 1 ? 'success' : 'danger'" size="mini" effect="plain">
            {{ currentRow.result === 1 ? '成功' : '失败' }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="操作时间">{{ currentRow.created_at }}</el-descriptions-item>
        <el-descriptions-item v-if="currentRow.fail_reason" label="失败原因" :span="2">
          {{ currentRow.fail_reason }}
        </el-descriptions-item>
        <el-descriptions-item label="客户端" :span="2">{{ currentRow.user_agent || '—' }}</el-descriptions-item>
        <el-descriptions-item label="请求参数" :span="2">
          <pre class="req-params">{{ formatParams(currentRow.request_params) }}</pre>
        </el-descriptions-item>
      </el-descriptions>
      <div slot="footer">
        <el-button size="small" @click="detailVisible = false">关 闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getOperationLogList, getOperationLogDetail, exportOperationLog } from '@/api/log'
import { exportCsv } from '@/util/exportCsv'

// 界面文案映射：内部标识 → 业务中文，页面不直接暴露内部对象名
const MODULE_TEXT = {
  activity: '活动管理',
  appointment: '预约管理',
  benefit: '权益券',
  company: '公司管理',
  stay: '旅居管理',
  content: '内容管理',
  system: '系统管理',
  user: '用户管理'
}
const OPERATION_TEXT = {
  create: '新增',
  update: '修改',
  delete: '删除',
  export: '导出',
  import: '导入',
  verify: '核销',
  audit: '审核'
}
const BIZ_TEXT = {
  activity: '活动',
  appointment: '预约',
  benefit: '权益券',
  company: '公司',
  room: '房型',
  content: '内容',
  person: '人员',
  dept: '部门',
  admin: '账号',
  role: '角色',
  menu: '菜单',
  auth: '认证'
}

export default {
  name: 'OperationLog',
  data() {
    return {
      loading: false,
      exporting: false,
      detailLoading: false,
      list: [],
      total: 0,
      query: { admin_name: '', module: '', operation: '', result: '' },
      page: 1,
      pageSize: 10,
      detailVisible: false,
      currentRow: null,
      modules: [
        { label: '活动管理', value: 'activity' },
        { label: '预约管理', value: 'appointment' },
        { label: '权益券', value: 'benefit' },
        { label: '公司管理', value: 'company' },
        { label: '旅居管理', value: 'stay' },
        { label: '内容管理', value: 'content' },
        { label: '系统管理', value: 'system' },
        { label: '用户管理', value: 'user' }
      ],
      operations: [
        { label: '新增', value: 'create' },
        { label: '修改', value: 'update' },
        { label: '删除', value: 'delete' },
        { label: '导出', value: 'export' },
        { label: '导入', value: 'import' },
        { label: '核销', value: 'verify' },
        { label: '审核', value: 'audit' }
      ]
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    // 列表（后端已做 admin_name 模糊 + module/operation/result 精确筛选，并分页）
    loadData() {
      this.loading = true
      const params = { page: this.page, page_size: this.pageSize }
      const adminName = (this.query.admin_name || '').trim()
      if (adminName) params.admin_name = adminName
      if (this.query.module) params.module = this.query.module
      if (this.query.operation) params.operation = this.query.operation
      if (this.query.result !== '' && this.query.result !== null) params.result = this.query.result
      getOperationLogList(params).then(res => {
        const data = res.data || {}
        this.list = data.list || []
        this.total = data.total || 0
      }).finally(() => {
        this.loading = false
      })
    },
    moduleLabel(v) { return MODULE_TEXT[v] || v },
    operationLabel(v) { return OPERATION_TEXT[v] || v },
    bizLabel(v) { return BIZ_TEXT[v] || v },
    operationTag(op) {
      const map = { create: 'success', update: 'primary', delete: 'danger', export: 'warning', import: 'warning', verify: 'info', audit: 'info' }
      return map[op] || 'info'
    },
    formatParams(params) {
      if (!params) return '—'
      if (typeof params === 'string') return params
      try {
        return JSON.stringify(params, null, 2)
      } catch (e) {
        return String(params)
      }
    },
    handleSearch() {
      this.page = 1
      this.loadData()
    },
    handlePageChange(val) {
      this.page = val
      this.loadData()
    },
    resetSearch() {
      this.query = { admin_name: '', module: '', operation: '', result: '' }
      this.page = 1
      this.loadData()
    },
    handleDetail(row) {
      this.detailVisible = true
      this.detailLoading = true
      this.currentRow = row
      getOperationLogDetail(row.id).then(res => {
        this.currentRow = res.data || row
      }).catch(() => {
        this.currentRow = row
      }).finally(() => {
        this.detailLoading = false
      })
    },
    // 导出：后端返回当前筛选下全部记录（最多 5000 条），前端拼成 CSV
    handleExport() {
      this.exporting = true
      const params = {}
      const adminName = (this.query.admin_name || '').trim()
      if (adminName) params.admin_name = adminName
      if (this.query.module) params.module = this.query.module
      if (this.query.operation) params.operation = this.query.operation
      if (this.query.result !== '' && this.query.result !== null) params.result = this.query.result
      exportOperationLog(params).then(res => {
        const rows = (res.data && res.data.list) || []
        if (rows.length === 0) {
          this.$message && this.$message.warning('当前筛选无数据可导出')
          return
        }
        const columns = [
          { key: 'admin_name', title: '操作人' },
          { key: 'module', title: '模块' },
          { key: 'operation', title: '操作类型' },
          { key: 'biz', title: '业务对象' },
          { key: 'request_url', title: '请求地址' },
          { key: 'method', title: '方法' },
          { key: 'ip', title: '操作IP' },
          { key: 'ip_location', title: '归属地' },
          { key: 'duration', title: '耗时(ms)' },
          { key: 'result', title: '结果' },
          { key: 'fail_reason', title: '失败原因' },
          { key: 'created_at', title: '操作时间' },
          { key: 'request_params', title: '请求参数' }
        ]
        const exportRows = rows.map(r => ({
          admin_name: r.admin_name,
          module: this.moduleLabel(r.module),
          operation: this.operationLabel(r.operation),
          biz: this.bizLabel(r.biz_type) + (r.biz_id ? '#' + r.biz_id : ''),
          request_url: r.request_url,
          method: r.method,
          ip: r.ip,
          ip_location: r.ip_location || '',
          duration: r.duration,
          result: r.result === 1 ? '成功' : '失败',
          fail_reason: r.fail_reason || '',
          created_at: r.created_at,
          request_params: r.request_params
        }))
        const stamp = new Date().toISOString().slice(0, 10)
        exportCsv(`操作日志_${stamp}.csv`, columns, exportRows)
      }).finally(() => {
        this.exporting = false
      })
    }
  }
}
</script>

<style scoped>
.cell-slow {
  color: #e6a23c;
}
.req-params {
  margin: 0;
  max-height: 220px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
  font-size: 12px;
  line-height: 1.6;
  background: #f7f8fa;
  padding: 8px 10px;
  border-radius: 4px;
}
</style>
